import { ClientProcess } from "@avenueops/shared-types";
import { Button, Tabs, TipTapEditor, Upload, UploadStatus, UploadSuccess, IconButton } from "@avenueops/web-components";
import { PaperAirplaneIcon } from "@heroicons/react/20/solid";
import { Role, CommentType } from "@prisma/client";
import { useMembership } from "context/OrganizationContext";
import { useState } from "react";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import Skeleton from "react-loading-skeleton";
import { toastError } from "utils/toast";
import { trpc } from "utils/trpc";
import { useFileUploadClient } from "utils/useFileUploadClient";

interface Props {
  process: ClientProcess;
}

export function CommentInput({ process }: Props) {
  const membership = useMembership();
  const role = membership?.role;
  const optionsFetchEnabled = role && role !== Role.GUEST;

  const [selectedTab, setSelectedTab] = useState<string>(CommentType.PUBLIC);

  const defaultValues = { commentMessage: "" };
  const formMethods = useForm({ defaultValues });
  const { reset, handleSubmit, formState, control } = formMethods;
  const [files, setFiles] = React.useState<Record<string, Upload>>();
  const [clearChatVal, setClearChatVal] = React.useState(() => () => {});
  const { isSubmitting } = formState;
  const utils = trpc.useContext();
  const fileUploadClient = useFileUploadClient();

  const { mutateAsync: createComment } = trpc.processComments.create.useMutation({
    onMutate: async () => {
      reset();
      clearChatVal();
    },
    onSuccess: async () => {
      await utils.processes.get.invalidate();
      await utils.processes.list.invalidate();
    },
  });

  const onSubmit = async (data: any, event: any) => {
    const fileIds = Object.values(files || {})
      .filter((file) => file.status === UploadStatus.Success)
      .map((file) => (file as UploadSuccess).metadata!.id!);

    try {
      await createComment({
        processId: process.id,
        commentMessage: data.commentMessage,
        attachmentIds: fileIds,
        commentType:
          selectedTab === CommentType.INTERNAL && (membership?.role === Role.ADMIN || membership?.role === Role.CREATOR)
            ? CommentType.INTERNAL
            : CommentType.PUBLIC,
      });
    } catch (err) {
      console.error(err);
      toastError(err, "There was an error when leaving a comment on this signal. Please try again later.");
    }
  };

  const onCommandEnter = (event: { key: string; metaKey: any }) => {
    if (event.key === "Enter" && event.metaKey) {
      handleSubmit(onSubmit)();
    }
  };

  const { isLoading, data: assigneeOptions } = trpc.assignees.listFromSignal.useQuery({ workflowProcessId: process.id }, { enabled: optionsFetchEnabled });

  const currEscalationGroupMentionables =
    assigneeOptions?.currentEscalationGroupAssignees.map((assignee) => ({
      value: assignee.email,
      label: `${assignee.firstName} ${assignee.lastName}`,
    })) ?? [];
  const otherMentionables =
    assigneeOptions?.otherAssignees.map((assignee) => ({
      value: assignee.email,
      label: `${assignee.firstName} ${assignee.lastName}`,
    })) ?? [];

  return isLoading && optionsFetchEnabled ? (
    <Skeleton count={1} />
  ) : (
    <div>
      {membership?.role === Role.CREATOR || membership?.role === Role.CUSTOMER_ENGINEER || membership?.role === Role.ADMIN ? (
        <div className="flex flex-row gap-5">
          <Tabs
            type="TEXT"
            options={[
              {
                label: "Public reply",
                value: CommentType.PUBLIC,
              },
              {
                label: "Internal note",
                value: CommentType.INTERNAL,
              },
            ]}
            selectedOption={selectedTab}
            setSelectedOption={setSelectedTab}
          />
        </div>
      ) : null}
      <div className="flex flex-row gap-2 max-w-full items-center">
        <div className="flex-1">
          <Controller
            name="commentMessage"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, name, ref } }) => (
              <div className={`min-h-[60px] flex`} ref={ref}>
                <TipTapEditor
                  name={name}
                  backgroundColor={selectedTab === CommentType.INTERNAL ? "bg-amber-50" : "bg-white"}
                  placeholder="Write a comment..."
                  onChange={(val, files) => {
                    onChange(val);
                    setFiles(files);
                  }}
                  mentionOptions={[...currEscalationGroupMentionables, ...otherMentionables]}
                  onKeyDown={onCommandEnter}
                  fileUploadClient={fileUploadClient}
                  setOuterResetFunction={setClearChatVal}
                />
              </div>
            )}
          />
        </div>
        <IconButton type="PRIMARY" Icon={PaperAirplaneIcon} onClick={handleSubmit(onSubmit)} submitting={isSubmitting} />
      </div>
    </div>
  );
}
